/* eslint-disable sort-keys */
/* eslint-disable max-len */
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import {
  useLocation,
  useNavigate
} from "react-router-dom";
import { shallow } from "zustand/shallow";
import { useCurrentEditionSession } from "./useCurrentEditionSession.hook";
import {
  HeroBanner,
  Modal,
  ModalEcmComponent,
  ScormTojModal,
  Spinner
} from "../../components";
import { useAnnouncement } from "../../components/Announcement/useAnnouncement";
import DetailsEsploraRestyle from "../../components/DetailsEsploraRestyle/DetailsEsploraRestyle.component";
import { getEditionJoinAvailability } from "../../components/TabsContent/Editions/utils";
// import { useEditions } from "../../components/LearningActiviyModal/useEditions.hook";
import {
  STATUS_LO,
  LEVEL,
  TAB_ITEMS_CONFIG
} from "../../consts";
import { UserNavigationContext } from "../../context/UserNavigationContext";
import { useLevel } from "../../hooks";
import {
  DescendantClassroomEdition,
  useDescendantsClassroomsQuery,
  useEditionsQuery,
  useLearningObjectQuery,
  useRelatedLearningObjectQuery,
  useToolsQuery
} from "../../queries";
import { useSharedPosts } from "../../queries/community";
// import { useGetExercisesList } from "../../queries/exercises";
import { useAuthStore, useCommunityClusterStore, useNavbarStore } from "../../stores";
import type {
  Course,
  JavaBasicDate,
  LearningActivity,
  ReferencedCourse,
  ReferencedLearningActivity,
  Root
} from "../../types";
import { hasPassedExpiration } from "../../utils/datetime";
import { getError } from "../../utils/error";
import {
  filterTabTypes
} from "../../utils/explore";
import { getPreviousRoute, isEnrollmentApprovalPending } from "../../utils/general";
import {
  getVisibleStructuredLearningObjects,
  normalizeLearningObjectStructure,
  type StructuredAlternativeSectionItem
} from "../../utils/learningObjectStructure";
import { useRouteParamsLo } from "../../utils/useRouteParamsLo";

export function ExploreDetail() {
  const isNotFoundCode = "core-005";
  const { t } = useTranslation();
  const { t: tAnnouncement } = useTranslation();
  const { announce } = useAnnouncement();
  const setSpinnerLoginVisible = useAuthStore(state => state.setSpinnerLoginVisible);
  setSpinnerLoginVisible(false);

  const { pathname } = useLocation();
  const navbarItem = useNavbarStore(state => state.item);

  const {
    idString: routeIdString,
    learningObjectTypology: routeLearningObjectTypology,
    idNumber: routeIdNumber,
    courseIdNumber: routeCourseIdNumber,
    rootIdNumber: routeRootIdNumber
  } = useRouteParamsLo();


  const hasParentContext =
    (!isNaN(routeRootIdNumber) && routeRootIdNumber !== routeIdNumber)
    || (!isNaN(routeCourseIdNumber) && routeCourseIdNumber !== routeIdNumber);

  const learningObjectTypology = !hasParentContext
    ? routeLearningObjectTypology
    : (!isNaN(routeRootIdNumber) && routeRootIdNumber !== routeIdNumber)
      ? LEVEL.PATH
      : LEVEL.COURSE;
  const idNumber = !hasParentContext
    ? routeIdNumber
    : learningObjectTypology === LEVEL.PATH ? routeRootIdNumber : routeCourseIdNumber;
  const idString = hasParentContext ? String(idNumber) : routeIdString;
  const courseIdNumber = !hasParentContext
    ? routeCourseIdNumber
    : learningObjectTypology === LEVEL.COURSE ? idNumber : NaN;
  const rootIdNumber = !hasParentContext
    ? routeRootIdNumber
    : learningObjectTypology === LEVEL.PATH ? idNumber : NaN;

  const [openModalEcm,setOpenModalEcm]=useState(false);
  const [openModalToj,setOpenModalToj]=useState(false);
  const [
    placeholderLO,
    setPlaceholderLO
  ] = useState<Course | LearningActivity | Root | undefined>(undefined);
  const [showModal, setShowModal] = useState(false);
  const [modalErrorText, setModalErrorText] = useState("");
  const navigate = useNavigate();
  const smartConfiguration = useAuthStore(state => state.smartConfiguration);
  const enableCommunityFlag = useAuthStore(state => state.corporateInfo?.enable_community_flag);
  const corporateId = useAuthStore(state => state.userData?.organization_id);
  const initiativeId = useAuthStore(state => state.userData?.initiative_id);
  const isAuthenticated = useAuthStore(state => Boolean(state.userData && state.userInfo));
  const { navigation, setNavigation } = useContext(UserNavigationContext);
  const isLearningActivity = (
    learningObjectTypology !== LEVEL.COURSE && learningObjectTypology !== LEVEL.PATH
  );

  useEffect(() => {
    document.body.removeAttribute("style");
    const isExpired = hasPassedExpiration(learningObject?.expirationDate);

    if (navigation?.includes(location.pathname) || (isLearningActivity && isExpired)) return;
    const currentNavigation = navigation;
    currentNavigation?.push(location.pathname);

    setNavigation && setNavigation(currentNavigation || []);
  }, [location.pathname]);

  const isCourse = (
    learningObjectTypology === LEVEL.COURSE
  );


  const {
    data: learningObject,
    error: learningObjectError,
    isFetching:learningObjectIsFetching,
    isError: learningObjectIsError,
    isSuccess: learningObjectIsSuccess,
    refetch:refetchLearningObject
  } = useLearningObjectQuery({
    courseId: courseIdNumber,
    enabled: Boolean(!isNaN(idNumber) && learningObjectTypology && corporateId && initiativeId),
    id: idNumber ?? "",
    learningObjectTypology: learningObjectTypology,
    placeholderData: placeholderLO?.id === idNumber ? placeholderLO : undefined,
    refetchOnMount: true,
    rootId: (isLearningActivity || isCourse ) ? rootIdNumber : null
  });


  const refetchLo = useCallback(() => {
    if (corporateId && initiativeId) {
      refetchLearningObject();
    }
  }, [corporateId, initiativeId, refetchLearningObject]);

  const {
    data: parentCourse,
    refetch:refetchParentLoCourse
  } = useLearningObjectQuery({
    enabled: Boolean(courseIdNumber && corporateId && initiativeId),
    id: courseIdNumber,
    learningObjectTypology: LEVEL.COURSE,
    rootId: (isLearningActivity || isCourse ) ? rootIdNumber : null
  });


  const refetchParentCourse = useCallback(() => {
    if (corporateId && initiativeId) {
      refetchParentLoCourse();
    }
  }, [corporateId, initiativeId, refetchParentLoCourse]);

  const isPathPage = learningObjectTypology === LEVEL.PATH;
  const courseOrPathIdForDescendants = isPathPage
    ? (!isNaN(rootIdNumber) ? rootIdNumber : 0)
    : (!isNaN(courseIdNumber) ? courseIdNumber : 0);

  const descendantsEnabled = (isPathPage || learningObjectTypology === LEVEL.COURSE)
    && courseOrPathIdForDescendants > 0;

  const {
    data: descendantsData,
    isLoading: descendantsLoading,
    isError: descendantsError
  } = useDescendantsClassroomsQuery({
    courseOrPathId: courseOrPathIdForDescendants,
    enabled: descendantsEnabled
  });

  // Aule presenti fra i discendenti, comprese quelle annidate nei corsi di un percorso:
  // `learningObject.learningActivities` porta i soli figli diretti e non basta al badge della hero.
  const hasDescendantClassrooms = (descendantsData?.activities ?? []).some(
    (descendant) => descendant.type === LEVEL.DA_VIRTUAL_CLASS
      || descendant.type === LEVEL.DA_PHYSICAL_CLASS
  );


  const { data: activityEditions } = useEditionsQuery({
    classroomId: learningObject?.id?.toString() ?? "",
    enabled:
      learningObject?.learningObjectTypology === LEVEL.DA_VIRTUAL_CLASS
      || learningObject?.learningObjectTypology === LEVEL.DA_PHYSICAL_CLASS
  });

  const enrolledEdition = activityEditions?.find((edition) => edition.status === "E");
  const { currentLink, isCurrentSession } = useCurrentEditionSession(enrolledEdition);

  const { data: related } = useRelatedLearningObjectQuery({
    learningObjectId: idString ?? ""
  });
  const { data: tools } = useToolsQuery({
    learningObjectId: learningObject?.id?.toString() ?? ""
  });

  const availableClusters = useCommunityClusterStore((store) => store.availableClusters, shallow);
  const communityFlag = enableCommunityFlag === true && smartConfiguration?.funcCommunity === true && !!(availableClusters.find(({ is_share_enabled }) => is_share_enabled));


  const { data: sharedPosts,
    isLoading:sharedPostIsLoading
  } = useSharedPosts({
    enabled: communityFlag,
    loId: idNumber });

  const normalizedContents = useMemo(() => {
    if (!learningObject) return null;
    if (
      learningObject.learningObjectTypology !== LEVEL.COURSE
      && learningObject.learningObjectTypology !== LEVEL.PATH
    ) return null;

    return normalizeLearningObjectStructure({
      courses: "courses" in learningObject ? learningObject.courses : [],
      learningActivities: learningObject.learningActivities ?? [],
      structure: "structure" in learningObject
        ? learningObject.structure
        : undefined
    });
  }, [learningObject]);

  const visibleStructuredContents = useMemo(
    () => getVisibleStructuredLearningObjects(normalizedContents?.items ?? []),
    [normalizedContents]
  );

  // POTREBBERO ESSERE LE ATTIVITà CONSIGLIATE???
  const getActivities = () => {
    if (normalizedContents) {
      return visibleStructuredContents.flatMap(({ entity }) =>
        entity.learningObjectTypology === LEVEL.COURSE
          ? []
          : [entity as ReferencedLearningActivity]
      );
    }
    if (learningObjectTypology !== LEVEL.COURSE && learningObjectTypology !== LEVEL.PATH) {
      if ((parentCourse && "learningActivities" in parentCourse)) {
        return parentCourse.learningActivities;
      }
    } else {
      if ((learningObject && "learningActivities" in learningObject)) {
        return learningObject.learningActivities;
      }
    }
  };

  const activities = getActivities() || [];

  const elementsToShow =
    learningObject?.learningObjectTypology ? (
      filterTabTypes({
        activities,
        // editions,
        // exercises,
        learningObject,
        related,
        sharedPosts,
        tabItems: TAB_ITEMS_CONFIG?.[learningObject?.learningObjectTypology].tabItems,
        tools,
        stakeholders: learningObject.stakeholders,
        hasCommunityEnabled: communityFlag
      })
    ) : [];

  const hasWrongType = useMemo(() => {
    if(learningObjectIsFetching || !learningObject?.learningObjectTypology || !learningObjectTypology) return false;
    return learningObject.learningObjectTypology !== learningObjectTypology;
  }, [learningObjectIsFetching, learningObject, learningObjectTypology]);


  const isNotFound = getError(learningObjectError)?.[0]?.cause === isNotFoundCode;
  const isNotEnrolled = getError(learningObjectError)?.[0]?.cause === "STUDENT_NOT_ENROLLED";

  if(isNotFound || hasWrongType){
    navigate("/not-found");
  }
  if (isNotEnrolled){
    navigate("/not-found", { state: { "message": t("student.not.enrolled") } });
  }

  useEffect(() => {
    if(learningObjectError && !isNotFound && !showModal) setShowModal(true);
  }, [learningObjectError, isNotFound, showModal]);

  useEffect(() => {
    if (learningObjectIsSuccess) {
      setPlaceholderLO(learningObject);
    }
  }, [learningObject, learningObjectIsSuccess]);
  const isToj = learningObject?.learningObjectTypology === LEVEL.DA_FINAL_BALANCE ||
  learningObject?.learningObjectTypology === LEVEL.DA_GOALS ||
  learningObject?.learningObjectTypology === LEVEL.DA_MATERIAL ||
  learningObject?.learningObjectTypology === LEVEL.DA_MEETING;

  useEffect(() => {
    function handleCloseModal(e: MessageEvent) {
      if (e.data !== "closeModal" || !isToj) return;

      setOpenModalToj(false);
      refetchLo();

      if (courseIdNumber) {
        refetchParentCourse();
      }
    }

    window.addEventListener("message", handleCloseModal);
    return () => {
      window.removeEventListener("message", handleCloseModal);
    };
  }, [courseIdNumber, isToj, refetchLo, refetchParentCourse]);

  const endDate = learningObject?.ecmEndDate ?
    [ learningObject?.ecmEndDate?.[0],
      learningObject?.ecmEndDate?.[1],
      learningObject?.ecmEndDate?.[2]
    ]  as JavaBasicDate :

    learningObject?.editionsTojDetails?.[0] ?
        [
          learningObject.editionsTojDetails[0].endTime?.[0],
          learningObject.editionsTojDetails[0].endTime?.[1],
          learningObject.editionsTojDetails[0].endTime?.[2]
        ]  as JavaBasicDate
      : undefined ;

  const startDate = learningObject?.ecmStartDate ?  [
    learningObject?.ecmStartDate?.[0],
    learningObject?.ecmStartDate?.[1],
    learningObject?.ecmStartDate?.[2]
  ] as JavaBasicDate :
    learningObject?.editionsTojDetails?.[0] ?
        [
          learningObject.editionsTojDetails[0].startTime?.[0],
          learningObject.editionsTojDetails[0].startTime?.[1],
          learningObject.editionsTojDetails[0].startTime?.[2]
        ]   as JavaBasicDate
      : undefined;


  useEffect(() => {
    if(learningObject?.title){
      announce( `${tAnnouncement("announcement_explore_detail")} ${learningObject?.title}`);
    }
  }, [learningObject?.title]);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {

    const handleVisibilityChange = () => {
      if (!document.hidden &&
      (  learningObjectTypology !== LEVEL.DA_PHYSICAL_CLASS &&
        learningObjectTypology !== LEVEL.DA_VIRTUAL_CLASS &&
        learningObjectTypology !== LEVEL.DA_MEETING
      )
      ){
        refetchLo();
        if(courseIdNumber){
          refetchParentCourse();
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const isVirtualClass = learningObject?.learningObjectTypology === LEVEL.DA_VIRTUAL_CLASS;

  // Iscrizione al corso/percorso concessa dal responsabile: finché è false le CTA dei figli
  // (aule e attività digitali) restano disabilitate. La cascata vale solo se questa pagina è contenitore (corso/percorso): un'attività standalone non ha un padre che possa
  // concederle l'approvazione, quindi ogni sua richiesta di iscrizione/cambio edizione deve
  // sempre passare da approvazione, a prescindere dal proprio stato passato.
  const isContainerPage = isCourse || isPathPage;
  const approvalGranted = isContainerPage
    ? !isEnrollmentApprovalPending(learningObject?.enrollType, learningObject?.status)
    : false;

  // Survey di gradimento ancora da compilare su un corso/percorso completato: prende il posto di
  // "Ricomincia" come CTA primaria della hero e porta direttamente al questionario.
  const pendingSatisfactionSurvey = useMemo(() => {
    if (learningObject?.status !== STATUS_LO.C) return undefined;
    if (!learningObject || !("learningActivities" in learningObject)) return undefined;
    const visibleActivities = normalizedContents
      ? visibleStructuredContents.flatMap(({ entity }) =>
        entity.learningObjectTypology === LEVEL.COURSE
          ? []
          : [entity as ReferencedLearningActivity]
      )
      : learningObject.learningActivities ?? [];
    return visibleActivities.find((activity) =>
      activity.isSurvey && activity.status !== STATUS_LO.C && activity.isEnable !== false
    );
  }, [learningObject, normalizedContents, visibleStructuredContents]);

  const surveyShortcut = pendingSatisfactionSurvey
    ? {
      id: pendingSatisfactionSurvey.id,
      learningObjectTypology: pendingSatisfactionSurvey.learningObjectTypology
    }
    : undefined;

  const joinAvailability = isVirtualClass && enrolledEdition
    ? getEditionJoinAvailability(enrolledEdition, true)
    : null;
  const editionJoinTooltipMessage = joinAvailability && joinAvailability !== "available"
    ? t(joinAvailability === "ended" ? "join_no_longer_available" : "join_not_available_yet")
    : undefined;
  //calcolo del numero attivita e corsi
  let cousesAndActivityNumberLabel = "";
  let stringCourse = "";
  let stringActivity = "";

  const structuredCourseItems = normalizedContents?.items.flatMap((item) => {
    if (item.kind !== "learning-object") return [];
    return item.entity.learningObjectTypology === LEVEL.COURSE
      ? [item.entity as ReferencedCourse]
      : [];
  }) ?? [];
  const structuredActivitySlots = normalizedContents?.items.filter((item) => {
    if (item.kind === "alternative-section") return true;
    return item.entity.learningObjectTypology !== LEVEL.COURSE;
  }).length ?? 0;

  const activitiesSum = normalizedContents
    ? structuredActivitySlots + structuredCourseItems.reduce(
      (sum, item) => sum + item.learningActivityNumber,
      0
    )
    : 0;
  const coursesSum = normalizedContents ? structuredCourseItems.length : 0;
  if(coursesSum === 1) {
    stringCourse = `${t("class_one",{ count: coursesSum })}`;
  }else if(coursesSum > 1) {
    stringCourse = `${t("class_other",{ count: coursesSum })}`;
  }

  if(activitiesSum === 1){
    stringActivity = `${activitiesSum} ${t("learning_activity")}`;
  }else if(activitiesSum > 1){
    stringActivity = `${t("activity_other",{ count:activitiesSum })}`;
  }

  if(stringCourse !== "" && stringActivity === ""){
    cousesAndActivityNumberLabel = stringCourse;
  }else if(stringCourse === "" && stringActivity !== ""){
    cousesAndActivityNumberLabel = stringActivity;
  }else if(stringCourse === "" && stringActivity === ""){
    cousesAndActivityNumberLabel = "";
  }else{
    cousesAndActivityNumberLabel = `${stringCourse } | ${stringActivity}`;
  }

  useEffect(() => {
    // Handler per eventi XAPI_TERMINATED
    const controller = new AbortController();
    type XapiMessageEvent = MessageEvent<{
      type: string;
      data: {
        endpoint: string;
        statement: unknown;
        headers: Record<string, string>;
      };
    }>;
    async function handleXapiMessage(event: XapiMessageEvent) {
      // Solo se il messaggio è quello atteso
      if (event.data && event.data.type === "XAPI_TERMINATED") {
        try {
          const res = await fetch(event.data.data.endpoint, {
            body: JSON.stringify(event.data.data.statement),
            headers: event.data.data.headers,
            keepalive: true,
            method: "POST",
            signal: controller.signal
          });
          // eslint-disable-next-line no-console
          console.log("xAPI response", res);
          // eslint-disable-next-line no-console
          console.log("xAPI statement sent successfully");
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error("Error sending xAPI statement:", error);
        } finally {
          // eslint-disable-next-line no-console
          console.log("dentro finally");
          window.removeEventListener("message", handleXapiMessage);
          controller.abort();
        }
      }
    }
    window.addEventListener("message", handleXapiMessage);
    return () => {
      window.removeEventListener("message", handleXapiMessage);
      controller.abort();
    };
  }, []);


  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mapActivities = (activities:any, isRequiredInStructure?: boolean, alternativeSection?: StructuredAlternativeSectionItem ) =>{

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return activities.map((activity:any) => ({
      attributes: activity.attributes,
      badge: activity.badge,
      canAccessNow: learningObject?.canAccessNow,
      certificate: activity.certificate,
      courseId: activity.courseId,
      disabled: !activity.isEnable || (!learningObject?.ecmRegistration && !!learningObject?.ecm_specialization),
      duration: activity.duration,
      ecmRegistration: false,
      ecm_specialization: false,
      enrollType: activity.enrollType,
      expirationDate: activity.expirationDate,
      externalFileUrl: activity.externalFileUrl,
      grandParentId: activity.grandParentId ?? undefined,
      id: activity.id,
      isEditorial: activity.isEditorial,
      isFavourite: activity.isFavourite,
      isLinkRelative: !!activity.isLinkRelative,
      isMandatory: activity.isMandatory,
      isRequiredInStructure,
      isStandAlone: activity.isStandAlone,
      isSurvey: activity.isSurvey,
      isTest: activity.isTest,
      learningObjectType: activity.learningObjectType,
      learningObjectTypology: activity.learningObjectTypology,
      link: activity.link,
      madeAttempts: activity.madeAttempts ?? undefined,
      maxAttempts: activity.maxAttempts,
      parentId: activity.parentId ?? undefined,
      percentageOfCompletion: activity.percentageOfCompletion,
      rootId: learningObject?.learningObjectTypology === "PATH" ? learningObject?.id : null,
      selfEnrollment: activity.selfEnrollment,
      enrollmentEnrollType: activity.enrollmentEnrollType,
      setOpenModalEcm,
      status: activity.status || "N",
      teachers: activity.teachers,
      title: activity.title,
      toolNumber: activity.toolNumber,
      editions:activity.editions,
      useful_links: activity.useful_links,
      ...(alternativeSection ? { alternativeSection, alternativeSectionId: alternativeSection.id } : {})
    }));
  };
  const allActivitiesCourses = normalizedContents
    ? normalizedContents.items.flatMap((item) => {
      if (item.kind === "alternative-section" && !item.selectedItem) return [item];
      const entity = item.kind === "learning-object" ? item.entity : item.selectedItem;
      if (!entity) return [];
      const isRequiredInStructure = item.kind === "learning-object"
        ? item.isRequiredInStructure
        : true;
      const alternativeSection = item.kind === "alternative-section"
        ? item
        : undefined;

      if (entity.learningObjectTypology === LEVEL.COURSE) {
        return [{
          ...entity,
          isRequiredInStructure,
          ...(alternativeSection == null
            ? {}
            : { alternativeSection, alternativeSectionId: alternativeSection.id })
        }];
      }
      return mapActivities(
        [entity],
        isRequiredInStructure,
        alternativeSection
      );
    })
    : learningObject?.learningObjectTypology === LEVEL.PATH && "courses" in learningObject
      ? [
        ...(learningObject.courses ?? []),
        ...mapActivities(learningObject.learningActivities ?? [])
      ]
      : learningObject && "learningActivities" in learningObject
        ? mapActivities(learningObject.learningActivities ?? [])
        : [];

  const sortedActivitiesCourses = normalizedContents
    ? allActivitiesCourses
    : [...allActivitiesCourses]
      .sort((a, b) => (a?.prereqOrderNumber ?? 0) - (b?.prereqOrderNumber ?? 0));

  const learningObjectTypologyType = useLevel(learningObjectTypology);

  const parseISOToJavaDate = (iso: string): [number, number, number] => {
    const [datePart] = iso.split("T");
    const [y, m, d] = datePart.split("-").map(Number);
    return [y, m, d];
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const activitiesWithEditionsRelated: any[] = (descendantsData?.activities ?? []).map((item) => {
    const hasGrandParent = item.grandParentObjectId != null && item.grandParentObjectId > 0;
    const courseId = hasGrandParent
      ? item.parentObjectId
      : isPathPage ? null : (item.parentObjectId > 0 ? item.parentObjectId : null);
    const rootId = hasGrandParent
      ? item.grandParentObjectId
      : isPathPage ? (item.parentObjectId > 0 ? item.parentObjectId : null) : null;

    return {
      id: item.idLo,
      title: item.title,
      shortDescription: item.description,
      learningObjectTypology: item.type,
      courseId,
      rootId,
      // `enrollStatus` resta "E" anche dopo la disiscrizione (descrive l'assegnazione dell'LO):
      // senza `editionId` l'utente non è iscritto ad alcuna edizione.
      status: item.editionId != null ? (item.enrollStatus || "N") : "N",
      enrollType: item.enrollType,
      selfEnrollment: item.selfEnrollment,
      editions: (item.editions ?? []).map((ed: DescendantClassroomEdition) => ({
        editionId: ed.editionId,
        startDate: parseISOToJavaDate(ed.startDate),
        endDate: parseISOToJavaDate(ed.endDate),
        status: ed.status,
        isHighlightEdition: false,
        teachers: []
      })),
      disabled: false,
      isFavourite: false,
      isStandAlone: false,
      isMandatory: false,
      isEditorial: false,
      isSurvey: false,
      isTest: false,
      isLinkRelative: false,
      canAccessNow: learningObject?.canAccessNow ?? false,
      attributes: [],
      teachers: [],
      tutors: []
    };
  });

  return (
    <>
      {
        ((!learningObject && learningObjectIsFetching) || !isAuthenticated) ? (
          <div
            style={ {
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100vh"
            } }
          >
            <Spinner />
          </div>
        ) :
          (
            isNotFound || !learningObject ? (
              <p>error occurred</p>
            ) : (
              <>
                <HeroBanner
                  attributes={ learningObject.attributes }
                  badge={ learningObject.badge }
                  booked={
                    (learningObject.status === STATUS_LO.E && learningObject.enrollmentEditionId != null)
                    || (learningObject.status === STATUS_LO.P)
                  }
                  completable={ learningObject.completable }
                  categories={ learningObject.topicTags || [] }
                  certificate={ learningObject.certificate }
                  certificates={ learningObject.certificates || [] }
                  coverVideoPublicUrl={ learningObject?.coverVideoPublicURL }
                  courseId={ courseIdNumber }
                  coverPublicURL={ learningObject.coverPublicURL?.toString() || "" }
                  duration={ learningObject.duration }
                  ecmType={ learningObject.ecmType }
                  expirationDate={ learningObject?.expirationDate }
                  endDate={ endDate }
                  enrollType={ learningObject.enrollType }
                  id={ learningObject?.id }
                  ecmRegistration={ learningObject.ecmRegistration }
                  ecm_specialization={ learningObject.ecm_specialization }
                  isEnable = { learningObject.isEnable }
                  isExpirationDateHard={ learningObject?.isExpirationDateHard }
                  isFavourite={ learningObject?.isFavourite }
                  isMandatory={ learningObject.isMandatory }
                  isSurvey={ "isSurvey" in learningObject ? learningObject.isSurvey : false }
                  isTest={  "isTest" in learningObject ? learningObject.isTest : false }
                  isToj={ learningObject?.isToj }
                  learningObjectType={ learningObject.learningObjectType }
                  learningObjectTypology={
                    learningObject.learningObjectTypology
                    || learningObjectTypology
                  }
                  meetingDetails={ learningObject?.meetingDetails }
                  percentageOfCompletion={ learningObject.percentageOfCompletion }
                  rating={ learningObject.averageRatingScore ?? 0 }
                  rootId={ rootIdNumber }
                  setOpenModalEcm={ setOpenModalEcm }
                  setOpenTojModal={ setOpenModalToj }
                  shortDescription={ learningObject.shortDescription }
                  startDate={ startDate }
                  status={ learningObject?.status }
                  title={ learningObject.title }
                  isCurrentSession={ isCurrentSession }
                  canAccessNow={ learningObject.canAccessNow }
                  isLinkRelative={ learningObject.isLinkRelative }
                  link={ learningObject.link }
                  credits={ learningObject.credits }
                  attributesIds={ learningObject.attributes }
                  editionsLength={ activityEditions?.length }
                  editionVirtualLink={ currentLink || null }
                  editionJoinTooltipMessage={ editionJoinTooltipMessage }
                  learningActivities={ learningObject.learningActivities }
                  hasDescendantClassrooms={ hasDescendantClassrooms }
                  surveyShortcut={ surveyShortcut }
                />
                <DetailsEsploraRestyle
                  learningObject={ learningObject }
                  title={ learningObject.title }
                  description={ learningObject.shortDescription }
                  idString={ idString ?? "" }
                  isLoading={ learningObjectIsFetching || sharedPostIsLoading }
                  isVirtualClass={ isVirtualClass }
                  cousesAndActivityNumberLabel={ cousesAndActivityNumberLabel }
                  sharedPosts={ sharedPosts }
                  tools={ tools }
                  related={ related }
                  activitiesWithEditionsRelated={ activitiesWithEditionsRelated  }
                  learningObjectTypologyType={ learningObjectTypologyType }
                  allActivitiesCoursesSorted={ sortedActivitiesCourses }
                  setOpenModalEcm={ setOpenModalEcm }
                  elementsToShow={ elementsToShow }
                  descendantsClassrooms={ descendantsData?.activities ?? [] }
                  descendantsLoading={ descendantsLoading }
                  descendantsUnavailable={ !descendantsEnabled || descendantsError }
                  approvalGranted={ approvalGranted }
                  enrollType={ learningObject?.enrollType }
                />
              </>
            )
          )
      }

      {
        learningObject && learningObject.enrollId && openModalEcm ?
          <ModalEcmComponent
            courseId={ courseIdNumber }
            open={ openModalEcm }
            invalidateLearningObects={ ()=> {
              refetchLo();
              if(courseIdNumber){
                refetchParentCourse();
              }

            } }
            loId={ idNumber }
            enrollId={ learningObject?.enrollId }
            onClose={ ()=> setOpenModalEcm(false) }
            rootId={ rootIdNumber }
            sponsors={ learningObject?.sponsors }
          /> : null
      }
      {
        openModalToj && learningObject ?

          <ScormTojModal
            activityLevel={ learningObject?.learningObjectTypology  }
            idString={ learningObject.id }
            open={ openModalToj }
            courseId={ courseIdNumber?.toString() ?? null }
            pathId={ rootIdNumber?.toString() ?? null }
          /> :
          null
      }
      <Modal
        description={ modalErrorText || (getError(learningObjectError)?.[0]?.message ?? "") }
        open={ showModal }
        title={
          modalErrorText
            ? t("attention")
            : getError(learningObjectError)?.[0]?.name === "FATAL"
              ? t("attention")
              : (getError(learningObjectError)?.[0]?.name ?? "")
        }
        onClose={ ()=> {
          setShowModal(false);
          setModalErrorText("");
          if (learningObjectIsError) {
            const previousRoute = getPreviousRoute({
              id: idNumber,
              rootId: rootIdNumber,
              courseId: courseIdNumber,
              path: pathname,
              section: navbarItem,
              learningObjectTypology
            });
            navigate(previousRoute);
          }
        } }
      />
    </>
  );
}
